import { ListItemIcon, MenuItem } from "@mui/material";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import clsx from "clsx";
import { ReactElement, useState } from "react";
import { MenuItemLinkClasses } from "react-admin";
import { useLocation } from "react-router-dom";

import { storage } from "../../../storage";
import { getRegionalResourceName, ResourceRegional } from "../../../types";
import { routes } from "../../../utils/Routes";
import {
  menuItemLinkSx as defaultMenuItemLinkSx,
  menuItemSxForActiveOrHover,
  menuItemSxForBorder,
  menuItemSxForLeftIcon,
  menuItemSxForText,
} from "./Common";

const flagContainerSx = {
  // Take enough space from parent to do a job as a click target
  height: "100%",
  width: "64px",

  // Helps aligning contained items centrally
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  // Refers to the currently hovered menu item.
  ...menuItemSxForActiveOrHover,

  // Refers to any img tags with the menu item
  "& img": {
    height: "13px",
    width: "20px",
  },
};

const menuItemLinkSx = {
  // Replicate required menu items styles for the container
  // taking care of clicks outside of the flags
  ...menuItemSxForLeftIcon,
  ...menuItemSxForText,

  // Setup dimensions & layout
  height: "100%",
  width: "100%",

  // Menu item isn't clickable (only the flags are)
  cursor: "default",

  "&:hover": {
    backgroundColor: "transparent",
  },
};

const outerContainerSx = {
  // Replicate required menu items styles for the outermost
  // container
  ...menuItemSxForActiveOrHover,
  ...menuItemSxForBorder,

  // Setup dimensions & layout
  height: defaultMenuItemLinkSx.height,
  display: "flex",
};

interface MultiRegionMenuItemLinkProps {
  leftIcon?: ReactElement;
  resource: ResourceRegional;
  buildPath?: (resourceName: string) => string;
  primaryText: string;
}

function MultiRegionMenuItemLink(props: MultiRegionMenuItemLinkProps) {
  const {
    leftIcon,
    resource,
    buildPath = routes.computeListFor,
    primaryText,
  } = props;

  const regionConfigs = storage.loadRegionConfigs();
  const { pathname } = useLocation();
  const [hoverRegion, setHoverRegion] = useState<string | null>(null);

  const regionPaths = regionConfigs.map((config) => ({
    config,
    path: buildPath(getRegionalResourceName(config.region, resource)),
  }));

  const isAnyActive = regionPaths.some(({ path }) =>
    pathname.startsWith(path.substring(1)),
  );

  return (
    <Box
      className={clsx({
        // Mark the whole menu as active if any region matches
        [MenuItemLinkClasses.active]: isAnyActive,
      })}
      sx={outerContainerSx}
    >
      <MenuItem sx={menuItemLinkSx}>
        {/* Use the same style as what react-admin uses for `MenuItemLink` */}
        {leftIcon && (
          <ListItemIcon className={MenuItemLinkClasses.icon}>
            {leftIcon}
          </ListItemIcon>
        )}
        {primaryText}
      </MenuItem>
      {regionPaths.map(({ config, path }) => {
        const isActive = pathname.startsWith(path.substring(1));
        const isHovered = hoverRegion === config.region;
        const iconUrl =
          isActive || isHovered
            ? config.region_icon_color_url
            : config.region_icon_gray_url;

        return (
          <Link
            key={config.region}
            href={path}
            sx={flagContainerSx}
            onMouseEnter={() => setHoverRegion(config.region)}
            onMouseLeave={() => setHoverRegion(null)}
          >
            <img src={iconUrl} alt={`${config.region_name} flag`} />
          </Link>
        );
      })}
    </Box>
  );
}

export default MultiRegionMenuItemLink;
